import { isNative } from '@/lib/platform'

/**
 * Whether this build may show anything that sells the product: prices, plan
 * pickers, "buy" buttons, links to a checkout.
 *
 * NOT the sign-up form — that moved to canShowSignupUi() below, because
 * creating a free account is not a sale. Do not reintroduce it here.
 *
 * FALSE ON NATIVE, AND THAT IS AN APP STORE REQUIREMENT, NOT A PREFERENCE.
 *
 * ZeroSnag does not use In-App Purchase. The carve-outs that let a B2B product
 * get away with that all hinge on the same condition — no purchasing inside
 * the app:
 *
 *   3.1.3(b) Multiplatform Services — the primary basis: users may "access
 *     content, subscriptions, or features they have acquired in your app on
 *     other platforms or your web site". The trailing "provided those items
 *     are also available as in-app purchases" clause attaches to consumable
 *     items in multi-platform GAMES, not to a SaaS subscription — this is the
 *     standard carve-out B2B companion apps ship under, and it doesn't care
 *     who paid.
 *   3.1.3(f) Free Stand-alone Apps — reinforces it: a free companion to a paid
 *     web tool needs no IAP "provided there is no purchasing inside the app,
 *     or calls to action for purchase outside of the app".
 *
 * Do NOT lean on 3.1.3(c) Enterprise Services: Apple narrowed it to an
 * access-only clause, and its "consumer, single user, or family sales must use
 * in-app purchase" line cuts against us — ZeroSnag plausibly sells one seat to
 * an individual Bauleiter.
 *
 * A "Lizenz erwerben" button next to a 49 €/month price inside the app defeats
 * all of the above at once and earns the standard 3.1.1 rejection.
 *
 * Selling still happens, just not in the binary: the web app has the full
 * self-service checkout, and the native app's users get a purchase link by
 * email, which the guidelines explicitly allow ("developers can send
 * communications outside of the app to their user base about purchasing
 * methods other than in-app purchase").
 *
 * The US storefront has been exempt from the anti-steering rule since the 2025
 * Epic injunction, but the EU is not, and EU/DE/AT/CH is the market. Anything
 * more permissive here needs the StoreKit External Purchase Link Entitlement
 * (application, addendum, disclosure sheet, Apple commission) first.
 */
export function canShowPurchaseUi(): boolean {
  return !isNative()
}

/**
 * Whether this build may show the sign-up form and the link that leads to it.
 *
 * TRUE EVERYWHERE, INCLUDING NATIVE — and that is a deliberate bet, not an
 * oversight. It used to be tied to canShowPurchaseUi(), which made the native
 * app a dead end: someone who downloaded it before creating an account had no
 * way forward and no hint that the account is made on the web.
 *
 * The argument for allowing it: creating a free account is not a purchase.
 * 3.1.3(f) forbids "purchasing inside the app, or calls to action for purchase
 * outside of the app" — a registration form that names no price, no plan and
 * no eventual cost is neither. Plenty of subscription apps ship exactly this.
 *
 * The argument against, which is why this is a separate switch: the trial does
 * convert to a paid licence later, and a reviewer may read the form as the
 * first step of a purchase funnel regardless of its wording.
 *
 * So it is kept apart from canShowPurchaseUi() on purpose. If App Review
 * rejects the build over this, the way back is `return !isNative()` here plus
 * the two tests in __tests__/unit/lib/purchase-surface.test.ts that pin the
 * current behaviour — they are supposed to go red when it changes, that is
 * what they are for. Nothing else moves: no call site, no copy, no route.
 *
 * The wording is load-bearing. Everything on the native sign-up path must stay
 * free of prices, plans and any reference to what it costs afterwards — see
 * the copy in RegisterForm and the neutral link label in LoginForm.
 */
export function canShowSignupUi(): boolean {
  return true
}
